import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import blogData from '../data/blogData';
import logoImg from '../assets/Logo.jpeg';
import '../styles/Blog.css';
import Seo from './Seo';

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const cardRefs = useRef([]);
  const headingRef = useRef(null);

  const categories = ['All', ...new Set(blogData.map(b => b.category))];

  const filteredBlogs = activeCategory === 'All'
    ? blogData
    : blogData.filter(b => b.category === activeCategory);

  // Scroll-aware nav
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // IntersectionObserver for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('blog-animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '-20px 0px' }
    );

    const elements = [headingRef.current, ...cardRefs.current].filter(Boolean);
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [filteredBlogs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('nav')) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  const navItems = [
    { name: "Home", href: "/" },
    { name: "The Game", href: "/#about-game" },
    { name: "Locations", href: "/#locations" },
    { name: "FAQs", href: "/#faqs" },
    { name: "Blog", href: "/blog" },
    { name: "The Architect", href: "/creator" }
  ];

  const featuredExternalBlogs = [
    {
      title: 'Mystery Mansion vs Escape Rooms: Which One Should You Choose?',
      excerpt: 'A closer look at why immersive mystery stays can feel more personal, social, and memorable than standard escape rooms.',
      url: 'https://wesellexperiences.blogspot.com/2026/07/mystery-mansion-vs-escape-rooms-which.html'
    },
    {
      title: 'Hidden Gems in Gurgaon That Most Locals Don’t Know About',
      excerpt: 'Discover unique experiences, offbeat spots, and unforgettable corners of Gurgaon that go beyond the usual weekend plans.',
      url: 'https://wesellexperiences.blogspot.com/2026/06/hidden-gems-in-gurgaon-most-locals-dont.html'
    }
  ];

  return (
    <div className="page-blog">
      <Seo
        path="/blog"
        title="Mystery Mansion Blog | Mysteries, Experiences & Things to Do"
        description="Explore Mystery Mansion stories and guides covering mystery experiences, unique group activities, staycations and things to do in Delhi NCR."
      />


      <div className="blog-page">
        {/* ===== NAVIGATION ===== */}
        <nav className={`fixed top-0 left-0 right-0 z-50 blog-nav ${scrolled ? 'is-scrolled' : ''}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16 md:h-18">
              <div className="flex-shrink-0">
                <Link to="/" style={{ textDecoration: 'none' }} className="inline-flex items-center gap-2">
                  <img src={logoImg} alt="Mystery Mansion" style={{
                    height: '32px', width: 'auto', objectFit: 'contain',
                    filter: 'drop-shadow(0 0 20px rgba(201, 168, 76, 0.15))',
                    borderRadius: '4px'
                  }} />
                </Link>
              </div>

              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-2">
                  {navItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.href}
                      className={`blog-nav-link ${item.href === '/blog' ? 'active' : ''}`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="md:hidden">
                <button
                  onClick={(e) => { e.stopPropagation(); setIsMenuOpen(!isMenuOpen); }}
                  className="focus:outline-none p-2"
                  aria-label="Toggle menu"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {isMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            <div className={`md:hidden blog-mobile-menu ${isMenuOpen ? 'open' : ''}`}>
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.href}
                    className={`blog-nav-link block py-3 ${item.href === '/blog' ? 'active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        {/* ===== HERO / HEADING ===== */}
        <div className="blog-hero" ref={headingRef}>
          <h1 className="blog-hero-title">The Archives</h1>
          <p className="blog-hero-subtitle">
            Mysteries. Stories. Secrets of the experience economy.
          </p>
          <div className="blog-gold-divider"></div>
        </div>

        {/* ===== FEATURED EXTERNAL BLOGS ===== */}
        <section className="blog-featured" aria-label="Featured blog links">
          <h2 className="blog-featured-title">Featured Posts</h2>
          <div className="blog-featured-grid">
            {featuredExternalBlogs.map((blog) => (
              <a
                key={blog.url}
                href={blog.url}
                target="_blank"
                rel="noreferrer"
                className="blog-feature-card"
              >
                <h3>{blog.title}</h3>
                <p>{blog.excerpt}</p>
              </a>
            ))}
          </div>
        </section>

        {/* ===== CATEGORY FILTERS ===== */}
        <div className="blog-filters">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`blog-filter-pill ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* ===== BLOG GRID ===== */}
        <div className="blog-grid">
          {filteredBlogs.map((blog, index) => (
            <Link
              to={`/blog/${blog.slug}`}
              key={blog.slug}
              className="blog-card blog-animate"
              ref={el => cardRefs.current[index] = el}
              style={{ textDecoration: 'none' }}
            >
              <div className="blog-card-img-wrap">
                <img
                  src={blog.heroImage}
                  alt={blog.heroAlt}
                  className="blog-card-img"
                  loading="lazy"
                />
                <span className="blog-card-category">{blog.category}</span>
              </div>
              <div className="blog-card-body">
                <h2 className="blog-card-title">{blog.title}</h2>
                <p className="blog-card-excerpt">{blog.excerpt}</p>
                <div className="blog-card-meta">
                  <span className="blog-card-date">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {blog.date}
                  </span>
                  <span className="blog-card-readtime">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {blog.readTime}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* ===== NEWSLETTER ===== */}
        <div className="blog-newsletter">
          <h3 className="blog-newsletter-title">🕵️ Get the Next Mystery</h3>
          <p className="blog-newsletter-desc">
            Get the next mystery delivered to your inbox. No spoilers.
          </p>
          <form className="blog-newsletter-form" onSubmit={(e) => { e.preventDefault(); alert('Thank you for subscribing! 🕵️'); }}>
            <input
              type="text"
              placeholder="Your name"
              className="blog-newsletter-input"
              required
              id="newsletter-name"
            />
            <input
              type="email"
              placeholder="Your email"
              className="blog-newsletter-input"
              required
              id="newsletter-email"
            />
            <button type="submit" className="blog-newsletter-btn">
              Subscribe
            </button>
          </form>
        </div>

        {/* ===== FOOTER ===== */}
        <footer className="blog-footer">
          <p className="blog-footer-text">
            © {new Date().getFullYear()} Mystery Mansion. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
}
